/* System Diagnostic & Resolution Center (STA-7 · Feb 2026)
   ==================================================================
   Central hub for every issue detected by EDRE. Any error surfaced
   anywhere in the system funnels here with the full 8-attribute
   contract: what · where · why · root_cause · how · one-click fix ·
   verification · prevention. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Activity, RefreshCw, Loader2, ShieldCheck, AlertTriangle, XCircle,
  Settings2, Info, Search,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import IssueCard from '../components/edre/IssueCard';

const SEV_ORDER = ['critical', 'warning', 'configuration', 'info'];
const SEV_COLOR = {
  critical:      { bg: 'bg-rose-50',   border: 'border-rose-300',   text: 'text-rose-800',   Icon: XCircle },
  warning:       { bg: 'bg-amber-50',  border: 'border-amber-300',  text: 'text-amber-800',  Icon: AlertTriangle },
  configuration: { bg: 'bg-slate-50',  border: 'border-slate-300',  text: 'text-slate-800',  Icon: Settings2 },
  info:          { bg: 'bg-sky-50',    border: 'border-sky-300',    text: 'text-sky-800',    Icon: Info },
};

export default function SystemDiagnosticCenter() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [scanSummary, setScanSummary] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/edre/issues');
      setIssues(data.issues || []);
    } catch (e) {
      toast.error('تعذَّر جلب الحالات');
    } finally { setLoading(false); }
  }, []);

  const runScan = async (silent = false) => {
    setScanning(true);
    try {
      const { data } = await axiosInstance.post('/edre/scan');
      setScanSummary(data);
      if (!silent) {
        const newCount = (data.new_detections || []).length;
        if (newCount > 0) toast.warning(`تم اكتشاف ${newCount} حالة جديدة`);
        else toast.success('اكتمل الفحص — لا حالات جديدة');
      }
      await load();
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || 'فشل الفحص');
    } finally { setScanning(false); }
  };

  useEffect(() => {
    // STA-7.2 · Silent auto-scan on mount refreshes stale issue
    // records with the latest detector logic (deep-links, root_cause
    // language, fix_execute_endpoints) — critical after a redeploy
    // because dedup preserves records but scan updates their content.
    runScan(true);
  }, []);

  const onResolved = (id) => {
    setIssues(is => is.filter(i => i.id !== id));
  };

  const byServerity = useMemo(() => {
    const acc = { critical: 0, warning: 0, configuration: 0, info: 0 };
    issues.forEach(i => { acc[i.severity] = (acc[i.severity] || 0) + 1; });
    return acc;
  }, [issues]);

  const filtered = useMemo(() => {
    let out = issues;
    if (filter !== 'all') out = out.filter(i => i.severity === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter(i =>
        (i.title_ar || '').toLowerCase().includes(q) ||
        (i.error_code || '').toLowerCase().includes(q) ||
        (i.location?.page || '').toLowerCase().includes(q));
    }
    // Sort: severity DESC, detected_at DESC
    out.sort((a, b) => {
      const ai = SEV_ORDER.indexOf(a.severity);
      const bi = SEV_ORDER.indexOf(b.severity);
      if (ai !== bi) return ai - bi;
      return (b.detected_at || '').localeCompare(a.detected_at || '');
    });
    return out;
  }, [issues, filter, search]);

  return (
    <div dir="rtl" className="max-w-7xl mx-auto p-6 space-y-4">
      {/* Header */}
      <div className="bg-white border-2 border-slate-900 rounded-lg p-5 flex items-center justify-between gap-4"
           data-testid="edre-header">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-slate-900 rounded-lg flex items-center justify-center text-white">
            <Activity size={26} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">System Diagnostic &amp; Resolution Center</h1>
            <p className="text-xs text-slate-600 mt-0.5">
              المرجع الوحيد لكل مشكلة — تشخيص · سبب جذري · تأثير · إصلاح بضغطة · تحقق · منع تكرار
            </p>
          </div>
        </div>
        <Button onClick={runScan} disabled={scanning} data-testid="edre-scan-btn"
                 className="bg-slate-900 hover:bg-slate-800 text-white">
          {scanning ? <Loader2 size={14} className="ml-2 animate-spin" /> : <RefreshCw size={14} className="ml-2" />}
          فحص شامل الآن
        </Button>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-4 gap-3">
        {SEV_ORDER.map(sev => {
          const meta = SEV_COLOR[sev];
          const { Icon } = meta;
          return (
            <button
              key={sev}
              onClick={() => setFilter(filter === sev ? 'all' : sev)}
              className={`rounded-lg border-2 p-3 text-right transition-all ${meta.bg} ${meta.border} ${filter === sev ? 'ring-2 ring-slate-900 ring-offset-2' : ''}`}
              data-testid={`edre-summary-${sev}`}>
              <div className={`flex items-center gap-2 ${meta.text}`}>
                <Icon size={16} />
                <div className="text-[10px] font-bold uppercase tracking-wider">
                  {{critical: 'حرج', warning: 'تحذير', configuration: 'إعداد', info: 'معلومات'}[sev]}
                </div>
              </div>
              <div className={`text-3xl font-bold mt-1 ${meta.text}`}>{byServerity[sev] || 0}</div>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          <Button size="sm" variant={filter === 'all' ? 'default' : 'outline'}
                  onClick={() => setFilter('all')} data-testid="filter-all">
            الكل ({issues.length})
          </Button>
          {SEV_ORDER.map(sev => (
            <Button key={sev} size="sm"
                    variant={filter === sev ? 'default' : 'outline'}
                    onClick={() => setFilter(sev)}
                    data-testid={`filter-${sev}`}
                    disabled={!byServerity[sev]}>
              {sev} ({byServerity[sev] || 0})
            </Button>
          ))}
        </div>
        <div className="flex-1 relative min-w-[200px]">
          <Search size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="ابحث في الحالات (بالعنوان، error_code، الصفحة)..."
            className="w-full text-xs border border-slate-300 rounded pr-7 pl-2 py-1.5"
            data-testid="edre-search"
          />
        </div>
      </div>

      {/* Issues list */}
      {loading ? (
        <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
          <Loader2 size={24} className="animate-spin mx-auto text-slate-400" />
          <div className="text-sm text-slate-500 mt-2">جارٍ التحميل...</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-8 text-center"
             data-testid="edre-empty">
          <ShieldCheck size={40} className="mx-auto text-emerald-600" />
          <div className="text-lg font-bold text-emerald-900 mt-2">
            {filter === 'all' ? 'لا توجد حالات مفتوحة' : `لا توجد حالات في هذا التصنيف`}
          </div>
          <p className="text-xs text-emerald-800 mt-2 max-w-md mx-auto">
            كل شيء تحت السيطرة. اضغط &quot;فحص شامل الآن&quot; لإعادة الفحص العميق لكل مصادر البيانات.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(issue => (
            <IssueCard key={issue.id} issue={issue} onResolved={onResolved} />
          ))}
        </div>
      )}

      {/* Scan summary footer */}
      {scanSummary && (
        <div className="text-[11px] text-slate-500 text-center bg-slate-50 border border-slate-200 rounded p-2 font-mono">
          آخر فحص: {scanSummary.detectors_ran} detector(s) شغِّلوا · اكتُشِف {(scanSummary.new_detections || []).length} حالة جديدة · إجمالي مفتوح: {scanSummary.total_open}
        </div>
      )}
    </div>
  );
}
